/**
 * Thin bridge to the Capacitor shell.
 *
 * Everything here talks to `window.Capacitor` at runtime rather than importing
 * the plugin packages. That keeps the web build free of native dependencies —
 * nothing to install, nothing to tree-shake, no build that can break because a
 * plugin is missing — and every function below is a no-op in a browser.
 *
 * Install the matching plugins in the native project (see docs/ANDROID.md) and
 * these light up on their own.
 */

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, any>;
}

const cap = (): CapacitorGlobal | undefined => (window as any).Capacitor;

/** True inside the Android or iOS shell, false in any browser. */
export const isNative = (): boolean => Boolean(cap()?.isNativePlatform?.());

/** 'android' | 'ios' | 'web'. */
export const platform = (): string => cap()?.getPlatform?.() ?? 'web';

const plugin = (name: string): any | undefined => cap()?.Plugins?.[name];

// --- the back button ------------------------------------------------------

/**
 * Android's back button has to do something sensible or the app just quits,
 * which is the fastest way to a one-star review.
 *
 * Screens and overlays push a handler; the most recently pushed one that
 * returns `true` has dealt with the press. If nothing claims it we are at the
 * top level, and that is the only place backing out of the app is right.
 */
type BackHandler = () => boolean;

const backStack: BackHandler[] = [];

export function pushBackHandler(fn: BackHandler): () => void {
  backStack.push(fn);
  return () => {
    const i = backStack.lastIndexOf(fn);
    if (i >= 0) backStack.splice(i, 1);
  };
}

/** Runs the stack top-down. Exported so it can be unit tested off-device. */
export function runBackHandlers(): boolean {
  for (let i = backStack.length - 1; i >= 0; i -= 1) {
    if (backStack[i]()) return true;
  }
  return false;
}

let backBound = false;

/**
 * @param onUnhandled called when nothing on the stack wanted the press — i.e.
 *   the user is at the top level and means to leave.
 */
export function bindBackButton(onUnhandled: () => void): void {
  if (backBound) return;
  const app = plugin('App');
  if (!app?.addListener) return;
  backBound = true;
  app.addListener('backButton', () => {
    if (!runBackHandlers()) onUnhandled();
  });
}

/** Close the app. Android only — iOS forbids it and Apple rejects apps that try. */
export function exitApp(): void {
  if (platform() !== 'android') return;
  plugin('App')?.exitApp?.();
}

// --- appearance -----------------------------------------------------------

/** Match the system bars to the paper the user chose, not the OS setting. */
export function setStatusBarTheme(dark: boolean): void {
  const bar = plugin('StatusBar');
  if (!bar) return;
  // Style.Dark means dark *content*, so it pairs with a light background.
  bar.setStyle?.({ style: dark ? 'DARK' : 'LIGHT' }).catch?.(() => {});
  if (platform() === 'android') {
    bar.setBackgroundColor?.({ color: dark ? '#0d1117' : '#f4f4f5' }).catch?.(() => {});
  }
}

export function hideSplash(): void {
  plugin('SplashScreen')?.hide?.().catch?.(() => {});
}

// --- lifecycle ------------------------------------------------------------

/**
 * Fires when the app is foregrounded or backgrounded. The socket layer already
 * reconnects on `visibilitychange`, but Android does not always emit that for
 * a WebView inside a paused activity, so this is the reliable signal.
 */
export function onAppStateChange(cb: (active: boolean) => void): () => void {
  const app = plugin('App');
  if (!app?.addListener) return () => {};
  const handle = app.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => cb(isActive));
  return () => { handle?.remove?.(); };
}

/**
 * Deep links, so tapping an inkshare.app/#/join/ABC123 link opens the app on
 * that pad instead of the browser.
 */
export function onDeepLink(cb: (url: string) => void): () => void {
  const app = plugin('App');
  if (!app?.addListener) return () => {};
  const handle = app.addListener('appUrlOpen', ({ url }: { url: string }) => cb(url));
  return () => { handle?.remove?.(); };
}

// --- haptics --------------------------------------------------------------

/** A short tick. Silently does nothing where the plugin is not installed. */
export function tick(): void {
  plugin('Haptics')?.impact?.({ style: 'LIGHT' }).catch?.(() => {});
}
